import { useSelector } from 'react-redux'
import { selectCustomerId, selectHomebotListingsFeatureEnabled } from '../store/selectors/customerProfile'
import { PATH_HELPERS } from '../constants/navigation'
import {
  ACTIVE,
  COMING_SOON,
  HOME_TYPES,
  MAX_PRICE,
  MAX_SQFT,
  MAX_YEAR_BUILT,
  MIN_BATHROOMS_COUNT,
  MIN_BEDROOMS_COUNT,
  MIN_PRICE,
  MIN_SQFT,
  MIN_YEAR_BUILT,
  ORDER,
  PROPERTY_TYPES,
  STATUSES
} from '../constants/listings'

// Constructs the url that will be navigated to when the user clicks on a listing
// in the carousel
const useViewAllListingsUrl = searchParams => {
  // Homebot listing source, route to homebot listing URLs
  const homebotListingsEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  const customerProfileId = useSelector(selectCustomerId)

  if (homebotListingsEnabled) {
    if (searchParams.zipcode) {
      return PATH_HELPERS.listings.buildPath(customerProfileId, 'postal_code', searchParams.zipcode, {
        statuses: ['Active', 'Coming Soon']
      })
    } else if (searchParams.placeId) {
      return PATH_HELPERS.listings.buildPath(customerProfileId, 'place_id', searchParams.placeId, {
        statuses: ['Active', 'Coming Soon']
      })
    }

    return PATH_HELPERS.listingsIndex.buildPath(customerProfileId)
  }
  return ''
}

const useViewListingShowPageUrl = listing => {
  const homebotListingsEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  const customerProfileId = useSelector(selectCustomerId)

  if (homebotListingsEnabled) {
    return PATH_HELPERS.listing.buildPath(customerProfileId, listing.id)
  }
  return ''
}

const useGetListingsByFilterVariables = filter => {
  const sqftExpression =
    filter?.[MIN_SQFT] && !filter?.[MAX_SQFT]
      ? { _gte: filter?.[MIN_SQFT] }
      : !filter?.[MIN_SQFT] && filter?.[MAX_SQFT]
      ? { _lte: filter?.[MAX_SQFT] }
      : filter?.[MIN_SQFT] && filter?.[MAX_SQFT]
      ? { _gte: filter?.[MIN_SQFT], _lte: filter?.[MAX_SQFT] }
      : {}

  const priceExpression =
    filter?.[MIN_PRICE] && !filter?.[MAX_PRICE]
      ? { _gte: filter?.[MIN_PRICE] }
      : !filter?.[MIN_PRICE] && filter?.[MAX_PRICE]
      ? { _lte: filter?.[MAX_PRICE] }
      : filter?.[MIN_PRICE] && filter?.[MAX_PRICE]
      ? { _gte: filter?.[MIN_PRICE], _lte: filter?.[MAX_PRICE] }
      : {}

  const yearExpression =
    filter?.[MIN_YEAR_BUILT] && !filter?.[MAX_YEAR_BUILT]
      ? { _gte: parseInt(filter?.[MIN_YEAR_BUILT], 10) }
      : !filter?.[MIN_YEAR_BUILT] && filter?.[MAX_YEAR_BUILT]
      ? { _lte: parseInt(filter?.[MAX_YEAR_BUILT], 10) }
      : filter?.[MIN_YEAR_BUILT] && filter?.[MAX_YEAR_BUILT]
      ? { _gte: parseInt(filter?.[MIN_YEAR_BUILT], 10), _lte: parseInt(filter?.[MAX_YEAR_BUILT], 10) }
      : {}

  const bedroomsExpression = filter?.[MIN_BEDROOMS_COUNT] ? { _gte: filter?.[MIN_BEDROOMS_COUNT] } : {}

  const bathroomsExpression = filter?.[MIN_BATHROOMS_COUNT] ? { _gte: filter?.[MIN_BATHROOMS_COUNT] } : {}

  const latitudeExpression =
    filter?.boundingBox?.bottom_right?.lat && filter?.boundingBox?.top_left?.lat
      ? { _gte: filter?.boundingBox?.bottom_right?.lat, _lte: filter?.boundingBox?.top_left?.lat }
      : {}

  const longitudeExpression =
    filter?.boundingBox?.top_left?.lon && filter?.boundingBox?.bottom_right?.lon
      ? { _gte: filter?.boundingBox?.top_left?.lon, _lte: filter?.boundingBox?.bottom_right?.lon }
      : {}

  const propertyTypes = filter?.[PROPERTY_TYPES]?.length > 0 ? filter?.[PROPERTY_TYPES] : HOME_TYPES
  const orderBy = filter?.[ORDER] || { DAYS_ON_MARKET: 'asc' }
  const statuses = filter?.[STATUSES]?.length > 0 ? filter?.[STATUSES] : [ACTIVE, COMING_SOON]

  return {
    sqft: sqftExpression,
    price: priceExpression,
    year: yearExpression,
    bedrooms: bedroomsExpression,
    bathrooms: bathroomsExpression,
    latitude: latitudeExpression,
    longitude: longitudeExpression,
    propertyTypes: propertyTypes,
    orderBy,
    statuses
  }
}

export { useViewAllListingsUrl, useViewListingShowPageUrl, useGetListingsByFilterVariables }
